import { useActiveConversationId } from "@app/hooks/useActiveConversationId";
import type { FileUploaderService } from "@app/hooks/useFileUploaderService";
import { useFileUploaderService } from "@app/hooks/useFileUploaderService";
import { useAuth } from "@app/lib/auth/AuthContext";
import type {
  RichAgentMention,
  RichMention,
} from "@app/types/assistant/mentions";
import type { ModelSelectionType } from "@app/types/assistant/models/types";
import { ModelSelectionSchema } from "@app/types/assistant/models/types";
import type { SkillWithoutInstructionsAndToolsType } from "@app/types/assistant/skill_configuration";
import type { ContentFragmentsType } from "@app/types/content_fragment";
import type { ComponentType, ReactNode } from "react";
import { createContext, useCallback, useMemo, useState } from "react";

const STICKY_MODEL_OVERRIDE_STORAGE_KEY = "inputBarModelOverride_v1";

function readStickyModelOverride(): ModelSelectionType | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    const raw = window.sessionStorage.getItem(
      STICKY_MODEL_OVERRIDE_STORAGE_KEY
    );
    if (!raw) {
      return undefined;
    }
    const parsed = ModelSelectionSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

function writeStickyModelOverride(selection: ModelSelectionType | undefined) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (selection === undefined) {
      window.sessionStorage.removeItem(STICKY_MODEL_OVERRIDE_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(
        STICKY_MODEL_OVERRIDE_STORAGE_KEY,
        JSON.stringify(selection)
      );
    }
  } catch {
    // Best-effort write only.
  }
}

/** Payload for the first message when creation is deferred until after navigation. */
export type PendingConversationMessage = {
  input: string;
  mentions: RichMention[];
  contentFragments: ContentFragmentsType;
  modelSelection?: ModelSelectionType;
};

/**
 * @cc [owner:adrsimon,label:react] typed-suffix-carries-no-tags
 * `typedSuffix` is revealed character by character after `text` is applied, so a caller MUST
 * put every serialized skill or tool tag in `text`: a partially revealed tag renders as raw
 * text instead of a chip.
 */
export type PendingInputText = {
  text: string;
  replace: boolean;
  typedSuffix?: string;
};

/**
 * What the user has selected in the app that hosts the input bar, e.g. cells in Excel. The input
 * bar shows it with the attachments, and its `label` is sent with the message as `hostSelection`.
 */
export type InputBarHostSelection = {
  label: string;
  icon: ComponentType<{ className?: string }>;
  onRemove: () => void;
};

export type PendingSkill = Pick<
  SkillWithoutInstructionsAndToolsType,
  "sId" | "name" | "icon"
>;

type CaptureActions = {
  onCapture: (type: "text" | "screenshot") => void;
  isCapturing: boolean;
  onSavePageToPod?: () => Promise<void>;
  isSavingPageToPod?: boolean;
};

export const InputBarContext = createContext<{
  shouldFocusInput: boolean;
  // One-shot agent mention for the input bar; InputBar clears after consuming.
  selectedAgent: RichAgentMention | null;
  setShouldFocusInput: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedAgent: (agentMention: RichAgentMention | null) => void;
  // Clears without touching shouldFocusInput (unlike setSelectedAgent(null)).
  clearSelectedAgent: () => void;
  selectedSingleAgent: RichAgentMention | null;
  setSelectedSingleAgent: (agentMention: RichAgentMention | null) => void;
  // When true, new-conversation default agent resolution is skipped (e.g. ?user=).
  suppressDefaultAgent: boolean;
  setSuppressDefaultAgent: (suppress: boolean) => void;
  // One-shot text for the input bar; InputBar clears after consuming.
  pendingInputText: PendingInputText | null;
  setPendingInputText: (
    text: string | null,
    options?: { replace?: boolean; typedSuffix?: string }
  ) => void;
  pendingSkill: PendingSkill | null;
  setPendingSkill: (skill: PendingSkill | null) => void;
  peekPendingFirstMessage: (
    conversationId: string
  ) => PendingConversationMessage | null;
  setPendingFirstMessage: (
    conversationId: string,
    message: PendingConversationMessage
  ) => void;
  clearPendingFirstMessage: (conversationId: string) => void;
  isLoadingGoTemplate: boolean;
  setIsLoadingGoTemplate: (loading: boolean) => void;
  stickyModelOverride: ModelSelectionType | undefined;
  setStickyModelOverride: (selection: ModelSelectionType | undefined) => void;
  fileUploaderService: FileUploaderService;
  captureActions?: CaptureActions;
  // Fired right before submit; the extension uses it to snapshot browser tab state.
  onBeforeSubmit?: () => void;
  hostSelection?: InputBarHostSelection | null;
}>({
  shouldFocusInput: false,
  selectedAgent: null,
  setShouldFocusInput: () => {},
  setSelectedAgent: () => {},
  clearSelectedAgent: () => {},
  selectedSingleAgent: null,
  setSelectedSingleAgent: () => {},
  suppressDefaultAgent: false,
  setSuppressDefaultAgent: () => {},
  pendingInputText: null,
  setPendingInputText: () => {},
  pendingSkill: null,
  setPendingSkill: () => {},
  peekPendingFirstMessage: () => null,
  setPendingFirstMessage: () => {},
  clearPendingFirstMessage: () => {},
  isLoadingGoTemplate: false,
  setIsLoadingGoTemplate: () => {},
  stickyModelOverride: undefined,
  setStickyModelOverride: () => {},
  fileUploaderService: {
    acceptedFileExtensions: [],
    fileBlobs: [],
    handleFileChange: async () => undefined,
    removeFile: () => {},
    addUploadedFile: () => {},
    getFileBlob: () => undefined,
    getFileBlobs: () => [],
    handleFilesUpload: async () => undefined,
    isProcessingFiles: false,
    resetUpload: () => {},
  },
});

interface InputBarContextProviderProps {
  children: ReactNode;
  fileUploaderService: FileUploaderService;
  captureActions?: CaptureActions;
  onBeforeSubmit?: () => void;
  hostSelection?: InputBarHostSelection | null;
}

export function InputBarContextProvider({
  children,
  fileUploaderService,
  captureActions,
  onBeforeSubmit,
  hostSelection,
}: InputBarContextProviderProps) {
  const [shouldFocusInput, setShouldFocusInput] = useState<boolean>(false);

  // Useful when a component needs to set the selected agent for the input bar but do not have direct access to the input bar.
  const [selectedAgent, setSelectedAgent] = useState<RichAgentMention | null>(
    null
  );

  // Skip new-conversation default agent (@dust / personal default), e.g. after ?user=.
  const [suppressDefaultAgent, setSuppressDefaultAgent] = useState(false);

  // Persistent agent selection for single-agent input mode (displayed in the agent picker button).
  const [selectedSingleAgent, setSelectedSingleAgentState] =
    useState<RichAgentMention | null>(null);

  const setSelectedSingleAgent = useCallback(
    (agentMention: RichAgentMention | null) => {
      if (agentMention) {
        // Explicit picker / URL agent selection re-enables default-agent resolution.
        setSuppressDefaultAgent(false);
      }
      setSelectedSingleAgentState(agentMention);
    },
    []
  );

  // Useful when a component needs to pre-fill the input bar with text.
  const [pendingInputText, setPendingInputTextState] =
    useState<PendingInputText | null>(null);
  const [pendingSkill, setPendingSkill] = useState<PendingSkill | null>(null);
  const [isLoadingGoTemplate, setIsLoadingGoTemplate] = useState(false);

  // Sticky model-picker override, hydrated from sessionStorage on mount and
  // written through on every change so it survives reloads within the tab.
  const [stickyModelOverride, setStickyModelOverrideState] = useState<
    ModelSelectionType | undefined
  >(() => readStickyModelOverride());

  const setStickyModelOverride = useCallback(
    (selection: ModelSelectionType | undefined) => {
      writeStickyModelOverride(selection);
      setStickyModelOverrideState(selection);
    },
    []
  );

  // First message stashed while navigating to a newly-created conversation (deferred-send flow).
  const [
    pendingFirstMessagesByConversation,
    setPendingFirstMessagesByConversation,
  ] = useState<Record<string, PendingConversationMessage>>({});

  const setPendingFirstMessage = useCallback(
    (conversationId: string, message: PendingConversationMessage) => {
      setPendingFirstMessagesByConversation((prev) => ({
        ...prev,
        [conversationId]: message,
      }));
    },
    []
  );

  const peekPendingFirstMessage = useCallback(
    (conversationId: string) =>
      pendingFirstMessagesByConversation[conversationId] ?? null,
    [pendingFirstMessagesByConversation]
  );

  const clearPendingFirstMessage = useCallback((conversationId: string) => {
    setPendingFirstMessagesByConversation((prev) => {
      const { [conversationId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const setSelectedAgentOuter = useCallback(
    (agentMention: RichAgentMention | null) => {
      if (agentMention) {
        setShouldFocusInput(true);
      } else {
        setShouldFocusInput(false);
      }
      setSelectedAgent(agentMention);
    },
    [setSelectedAgent]
  );

  const clearSelectedAgent = useCallback(() => {
    setSelectedAgent(null);
  }, []);

  const setPendingInputText = useCallback(
    (
      text: string | null,
      options?: { replace?: boolean; typedSuffix?: string }
    ) => {
      if (text === null) {
        setPendingInputTextState(null);
        return;
      }
      setPendingInputTextState({
        text,
        replace: options?.replace ?? false,
        typedSuffix: options?.typedSuffix,
      });
    },
    []
  );

  const value = useMemo(
    () => ({
      shouldFocusInput,
      setShouldFocusInput,
      selectedAgent,
      setSelectedAgent: setSelectedAgentOuter,
      clearSelectedAgent,
      selectedSingleAgent,
      setSelectedSingleAgent,
      suppressDefaultAgent,
      setSuppressDefaultAgent,
      pendingInputText,
      setPendingInputText,
      pendingSkill,
      setPendingSkill,
      peekPendingFirstMessage,
      setPendingFirstMessage,
      clearPendingFirstMessage,
      isLoadingGoTemplate,
      setIsLoadingGoTemplate,
      stickyModelOverride,
      setStickyModelOverride,
      captureActions,
      fileUploaderService,
      onBeforeSubmit,
      hostSelection,
    }),
    [
      shouldFocusInput,
      selectedAgent,
      setSelectedAgentOuter,
      clearSelectedAgent,
      selectedSingleAgent,
      setSelectedSingleAgent,
      suppressDefaultAgent,
      pendingInputText,
      setPendingInputText,
      pendingSkill,
      peekPendingFirstMessage,
      setPendingFirstMessage,
      clearPendingFirstMessage,
      isLoadingGoTemplate,
      stickyModelOverride,
      setStickyModelOverride,
      captureActions,
      fileUploaderService,
      onBeforeSubmit,
      hostSelection,
    ]
  );

  return (
    <InputBarContext.Provider value={value}>
      {children}
    </InputBarContext.Provider>
  );
}
interface InputBarProviderProps {
  children: ReactNode;
}

export function InputBarProvider({ children }: InputBarProviderProps) {
  const conversationId = useActiveConversationId();

  const { workspace } = useAuth();

  const useCaseMetadata = useMemo(() => {
    if (!conversationId) {
      return undefined;
    }
    return {
      conversationId,
    };
  }, [conversationId]);

  const fileUploaderService = useFileUploaderService({
    hasSandboxTools: true,
    owner: workspace,
    useCase: "conversation",
    useCaseMetadata,
  });

  // Reset fileBlobs when conversationId changes.
  // We intentionally avoid using a key prop as it would remount
  // the entire page subtree (InputBarStateProvider wraps children)
  // just to reset a single array.
  const [prevConversationId, setPrevConversationId] = useState(conversationId);
  if (conversationId !== prevConversationId) {
    setPrevConversationId(conversationId);
    fileUploaderService.resetUpload();
  }

  return (
    <InputBarContextProvider fileUploaderService={fileUploaderService}>
      {children}
    </InputBarContextProvider>
  );
}
